import {
  buildRawCaptureUserFacingCategories,
  getPrimaryRawCaptureUserFacingCategory,
} from './rawCaptureCategoryPreview';

const FACT_KEYS = [
  'sleep',
  'food',
  'medication',
  'behavior',
  'mood',
  'activity',
  'health',
  'environment',
  'people',
];

const EMPTY_FACTS = FACT_KEYS.reduce((facts, key) => ({
  ...facts,
  [key]: [],
}), {});

const MONTH_DATE_PATTERN = '\\b(?:Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:t(?:ember)?)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)\\s+\\d{1,2}(?:,\\s*\\d{4})?\\b';
const NUMERIC_DATE_PATTERN = '\\b\\d{1,2}[/-]\\d{1,2}(?:[/-]\\d{2,4})?\\b';
const RELATIVE_DATE_PATTERN = '\\b(?:today|yesterday|tomorrow)\\b';
const DATE_PATTERN = new RegExp(`${MONTH_DATE_PATTERN}|${NUMERIC_DATE_PATTERN}|${RELATIVE_DATE_PATTERN}`, 'i');
const TIME_PATTERN = /\b(?:around|about|at|by)?\s*(?:\d{1,2}(?::\d{2})?\s*(?:AM|PM|am|pm)|midnight|noon)\b/;

const VALID_SOURCE_TYPES = new Set(['text', 'voice', 'import', 'unknown']);

const KEYWORD_RULES = {
  sleep: [
    /\b(?:woke up|wake up|awake|asleep|slept|sleep|sleeping|bedtime|nap|restfully|poorly|tired)\b/i,
  ],
  food: [
    /\b(?:breakfast|lunch|dinner|snack|meal|ate|eating|appetite|drank|smoothie|crackers|popcorn|food)\b/i,
  ],
  medication: [
    /\b(?:medication|medications|meds|dose|given|took|depakote|guanfacine|lorazepam|prescription)\b/i,
  ],
  behavior: [
    /\b(?:aggression|aggressive|hit|hitting|pushed|threw|yelling|crying|refused|meltdown|upset|overwhelmed|dysregulated|level\s+\d)\b/i,
  ],
  mood: [
    /\b(?:calm|calmer|happy|happier|cheerful|excited|emotional|affectionate|anxious|shy|frustrated)\b/i,
  ],
  activity: [
    /\b(?:walk|walking|park|outside|school|therapy|OT|ABA|music|sensory activities|watched|played|grocery|visited|going out)\b/i,
  ],
  health: [
    /\b(?:doctor|psychiatrist|appointment|follow-up|rash|fever|pain|sick|appetite changes|health)\b/i,
  ],
  environment: [
    /\b(?:degrees|weather|overcast|drizzling|drizzle|rain|rainy|sunny|cloudy|hot|cold|humid|bright|loud|quiet room|weighted blanket|temperature)\b/i,
  ],
  people: [
    /\b(?:mom|dad|parent|caregiver|teacher|therapist|doctor|psychiatrist|grandmother|grandpa|Aaron|Dr\.?\s+[A-Z][a-z]+)\b/,
  ],
};

const RECORD_TYPE_LABELS = {
  medication_admin: 'Medication context',
  sleep_period: 'Sleep observation',
  food_intake: 'Food intake',
  health_note: 'Health note',
  appointment: 'Appointment note',
  milestone: 'Milestone',
  routine: 'Routine note',
  event: 'Care event',
  observation: 'Care observation',
  general_note: 'General note',
};

export const extractCareTimelineRecords = (rawInput, options = {}) => {
  const source = buildSource(rawInput, options);
  const segments = splitIntoRecordSegments(source.rawText);

  return {
    source,
    records: segments.map((segment) => buildRecord(segment)),
  };
};

const buildSource = (rawInput, options) => {
  const isObjectInput = rawInput && typeof rawInput === 'object';
  const rawText = isObjectInput
    ? String(rawInput.rawText || rawInput.text || rawInput.note || '')
    : String(rawInput || '');
  const requestedSourceType = isObjectInput
    ? rawInput.sourceType || options.sourceType
    : options.sourceType || 'text';
  const sourceType = VALID_SOURCE_TYPES.has(requestedSourceType)
    ? requestedSourceType
    : 'unknown';

  return {
    rawText,
    sourceType,
    capturedAt: (isObjectInput && rawInput.capturedAt) || options.capturedAt || null,
  };
};

const splitIntoRecordSegments = (rawText) => {
  const trimmed = rawText.trim();
  if (!trimmed) {
    return [];
  }

  const paragraphs = trimmed
    .split(/\n\s*\n+/)
    .map((segment) => segment.trim())
    .filter(Boolean);
  if (paragraphs.length > 1) {
    return paragraphs;
  }

  const lines = trimmed
    .split(/\n+/)
    .map((segment) => segment.trim())
    .filter(Boolean);
  if (lines.length > 1) {
    return lines;
  }

  return splitInlineDatedRecords(trimmed);
};

const splitInlineDatedRecords = (text) => {
  const dateStartPattern = new RegExp(`(?=${MONTH_DATE_PATTERN}|${NUMERIC_DATE_PATTERN})`, 'gi');
  const parts = text
    .split(dateStartPattern)
    .map((segment) => segment.trim())
    .filter(Boolean);

  return parts.length > 1 ? parts : [text];
};

const buildRecord = (originalText) => {
  const facts = extractFacts(originalText);
  const date = extractDate(originalText);
  const time = extractTime(originalText);
  const recordType = inferRecordType(facts, originalText);
  const userFacingCategories = buildRawCaptureUserFacingCategories({ recordType, facts });
  const populatedFactCount = countPopulatedFacts(facts);
  const confidence = inferConfidence({ date, time, populatedFactCount });

  return {
    recordType,
    userFacingCategory: getPrimaryRawCaptureUserFacingCategory(userFacingCategories),
    userFacingCategories,
    title: buildTitle({ recordType, date, facts }),
    originalText,
    date,
    time,
    timeConfidence: time ? 'explicit' : 'unknown',
    whatHappened: buildWhatHappened(originalText, { date, time }),
    facts,
    confidence,
    needsReview: confidence === 'low',
  };
};

const extractFacts = (text) => {
  const facts = { ...EMPTY_FACTS };

  FACT_KEYS.forEach((key) => {
    facts[key] = extractFactPhrases(text, KEYWORD_RULES[key]);
  });

  facts.medication = addMedicationNames(text, facts.medication);
  facts.environment = addWeatherDetails(text, facts.environment);
  facts.people = addPersonNames(text, facts.people);

  return facts;
};

const extractFactPhrases = (text, rules) => unique(
  splitClauses(text).filter((clause) => rules.some((rule) => rule.test(clause)))
);

const splitClauses = (text) => text
  .split(/(?:,|\band\b|\bthen\b|\bafterward\b|\blater\b|\bbut\b)/i)
  .map((clause) => cleanPhrase(clause))
  .filter(Boolean);

const addMedicationNames = (text, medicationFacts) => {
  const medicationNames = unique((text.match(/\b(?:Depakote|Guanfacine|Lorazepam)\b/gi) || [])
    .map((name) => cleanPhrase(name)));
  return unique([...medicationFacts, ...medicationNames]);
};

const addWeatherDetails = (text, environmentFacts) => {
  const weatherDetails = [
    text.match(/\baround\s+\d+\s+degrees\b/i)?.[0],
    text.match(/\b\d+\s+degrees\b/i)?.[0],
    text.match(/\bovercast\b/i)?.[0],
    text.match(/\bdrizzling(?:\s+a\s+little)?\b/i)?.[0],
  ].filter(Boolean).map((detail) => cleanPhrase(detail));

  return unique([...environmentFacts, ...weatherDetails]);
};

const addPersonNames = (text, peopleFacts) => {
  const capitalizedNames = (text.match(/\b[A-Z][a-z]+(?:\s+[A-Z][a-z]+)?\b/g) || [])
    .filter((value) => !DATE_PATTERN.test(value))
    .filter((value) => !/^(AM|PM|Today|Yesterday|Tomorrow|May|March|April|January|February|Depakote|Guanfacine|Lorazepam)$/i.test(value))
    .map((value) => cleanPhrase(value));

  return unique([...peopleFacts, ...capitalizedNames]);
};

const extractDate = (text) => cleanPhrase(text.match(DATE_PATTERN)?.[0] || '') || null;

const extractTime = (text) => cleanPhrase(text.match(TIME_PATTERN)?.[0] || '') || null;

const inferRecordType = (facts, text) => {
  if (/\b(?:appointment|follow-up)\b/i.test(text)) {
    return 'appointment';
  }
  if (/\bmilestone\b/i.test(text)) {
    return 'milestone';
  }
  if (!facts.behavior.length && /\b(?:routine|as usual|usual morning|morning routine|bedtime)\b/i.test(text)) {
    return 'routine';
  }
  if (facts.medication.length && countPopulatedFacts(facts) <= 2) {
    return 'medication_admin';
  }
  if (facts.sleep.length && countPopulatedFacts(facts) <= 2) {
    return 'sleep_period';
  }
  if (facts.food.length && countPopulatedFacts(facts) <= 2) {
    return 'food_intake';
  }
  if (facts.health.length && countPopulatedFacts(facts) <= 2) {
    return 'health_note';
  }
  if (facts.behavior.length) {
    return 'event';
  }
  if (countPopulatedFacts(facts) > 0) {
    return 'observation';
  }
  return 'general_note';
};

const buildTitle = ({ recordType, date, facts }) => {
  const label = RECORD_TYPE_LABELS[recordType] || RECORD_TYPE_LABELS.general_note;
  const strongestFact = FACT_KEYS.find((key) => facts[key].length);
  const detail = strongestFact ? `${capitalize(strongestFact)} noted` : null;
  return [date, detail || label].filter(Boolean).join(' - ');
};

const buildWhatHappened = (text, { date, time }) => {
  let value = text.trim();
  [date, time].filter(Boolean).forEach((prefix) => {
    value = value.replace(new RegExp(`^${escapeRegExp(prefix)}\\s*`, 'i'), '');
  });
  return value.trim() || text.trim();
};

const inferConfidence = ({ date, time, populatedFactCount }) => {
  if (populatedFactCount >= 2 || (populatedFactCount >= 1 && (date || time))) {
    return 'high';
  }
  if (populatedFactCount === 1) {
    return 'medium';
  }
  return 'low';
};

const countPopulatedFacts = (facts) => FACT_KEYS.filter((key) => facts[key].length > 0).length;

const cleanPhrase = (value) => String(value || '')
  .replace(/\s+/g, ' ')
  .replace(/^[\s,.;:-]+|[\s,.;:-]+$/g, '')
  .trim();

const unique = (values) => Array.from(new Set(values.filter(Boolean)));

const capitalize = (value) => value.charAt(0).toUpperCase() + value.slice(1);

const escapeRegExp = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
